import { Button, InlineNotification, RadioButton, RadioButtonGroup, Stack, TextArea } from '@carbon/react';
import { Renew, Upload } from '@carbon/icons-react';
import type { HoComponents } from '@pos/api-client';
import {
  asProblem,
  hasPermission,
  ListTable,
  problemMessage,
  RuleBuilder,
  TrendChart,
  useAdmin,
  useFormat,
  type FieldEditorProps,
  type RecordValues,
} from '@pos/admin-ui';
import { useTranslation } from '@pos/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { hoApi } from '../api';
import { ErrorNotice } from '../common/Notices';
import { CsvDownloadButton } from '../promotions/CsvDownloadButton';
import { toListItem, type ListItem } from './segmentList';

type Person = HoComponents['schemas']['SegmentPersonDto'];

async function unwrap<T>(call: Promise<{ data?: T; error?: unknown }>): Promise<T> {
  const { data, error } = await call;
  if (data === undefined) throw error;
  return data;
}

/**
 * Who is in a segment (ADR 0080): its trend over the last 90 days and its people a page at a time, their phone and email masked
 * (rule 14). Worked out daily and on "refresh now"; nothing here changes who is in it.
 */
export function SegmentPeopleTab({ segment }: { segment: RecordValues }) {
  const { t } = useTranslation();
  const format = useFormat();
  const id = String(segment.id);
  const [paging, setPaging] = useState({ page: 1, pageSize: 25 });
  const people = useQuery({
    queryKey: ['crm', 'segment-people', id, paging],
    queryFn: () =>
      unwrap(
        hoApi.GET('/api/v1/crm/segments/{id}/people', {
          params: { path: { id }, query: { page: paging.page, pageSize: paging.pageSize } },
        }),
      ),
  });
  const trend = useQuery({
    queryKey: ['crm', 'segment-trend', id],
    queryFn: () => unwrap(hoApi.GET('/api/v1/crm/segments/{id}/trend', { params: { path: { id } } })),
  });
  const points = (trend.data ?? []).map((p) => ({ label: format.date(p.day), value: Number(p.people) }));

  return (
    <Stack gap={6}>
      {points.length > 1 && (
        <TrendChart
          type="line"
          points={points}
          labelHeader={t('crm.segments.trend.day')}
          valueHeader={t('crm.segments.trend.people')}
          summary={t('crm.segments.trend.summary', { count: points[points.length - 1]?.value ?? 0 })}
          formatValue={(v) => format.number(v)}
        />
      )}
      <ListTable<Person & { id: string }>
        ariaLabel={t('crm.segments.people.title')}
        loading={people.isLoading}
        error={people.error ? problemMessage(t, asProblem(people.error)) : undefined}
        onRetry={() => void people.refetch()}
        rows={(people.data?.items ?? []).map((p) => ({ ...p, id: p.profileId }))}
        pagination={{ ...paging, total: Number(people.data?.total ?? 0), onChange: setPaging }}
        empty={{ heading: t('crm.segments.people.empty'), description: t('crm.segments.people.emptyHint') }}
        columns={[
          {
            key: 'name',
            header: t('crm.segments.people.name'),
            render: (p) => p.name ?? t('crm.segments.people.noName'),
          },
          { key: 'phone', header: t('crm.segments.people.phone'), render: (p) => p.phone ?? '' },
          { key: 'email', header: t('crm.segments.people.email'), render: (p) => p.email ?? '' },
          {
            key: 'isMember',
            header: t('crm.segments.people.kind'),
            render: (p) => (p.isMember ? t('crm.segments.people.member') : t('crm.segments.people.guest')),
          },
          { key: 'orders365', header: t('crm.segments.people.orders'), render: (p) => format.number(p.orders365) },
          { key: 'spend365', header: t('crm.segments.people.spend'), render: (p) => format.number(p.spend365) },
          {
            key: 'lastOrderAt',
            header: t('crm.segments.people.lastOrder'),
            render: (p) => format.dateTime(p.lastOrderAt),
          },
        ]}
      />
    </Stack>
  );
}

/**
 * An uploaded-list segment's people (ADR 0080): phones, emails or member account numbers, one per line. Phones and emails are
 * kept at HO as keys only; unknown account numbers are named back. Other segments explain they don't take a list.
 */
export function SegmentListTab({ segment }: { segment: RecordValues }) {
  const { t } = useTranslation();
  const { session } = useAdmin();
  const id = String(segment.id);
  const [text, setText] = useState('');
  const [mode, setMode] = useState<'add' | 'replace'>('add');
  const queryClient = useQueryClient();
  const upload = useMutation({
    mutationFn: (entries: ListItem[]) =>
      unwrap(
        hoApi.PUT('/api/v1/crm/segments/{id}/list', {
          params: { path: { id } },
          body: { entries, replace: mode === 'replace' },
        }),
      ),
    onSuccess: () => {
      setText('');
      void queryClient.invalidateQueries({ queryKey: ['crm'] });
    },
  });

  if (segment.kind !== 'list') {
    return <InlineNotification kind="info" lowContrast hideCloseButton title={t('crm.segments.list.notList')} />;
  }

  const entries = text
    .split(/\r?\n/)
    .map(toListItem)
    .filter((e): e is ListItem => e !== null);
  const canManage = hasPermission(session, 'ho.crm.manage');
  return (
    <Stack gap={5}>
      <TextArea
        id="segment-list"
        labelText={t('crm.segments.list.label')}
        helperText={t('crm.segments.list.help')}
        rows={10}
        value={text}
        disabled={!canManage}
        onChange={(e) => setText(e.target.value)}
      />
      <RadioButtonGroup
        legendText={t('crm.segments.list.mode')}
        name="segment-list-mode"
        valueSelected={mode}
        onChange={(value) => setMode(value as 'add' | 'replace')}
      >
        <RadioButton id="segment-list-add" labelText={t('crm.segments.list.add')} value="add" />
        <RadioButton id="segment-list-replace" labelText={t('crm.segments.list.replace')} value="replace" />
      </RadioButtonGroup>
      <div>
        <Button
          renderIcon={Upload}
          disabled={!canManage || entries.length === 0 || entries.length > 10_000 || upload.isPending}
          onClick={() => upload.mutate(entries)}
        >
          {t('crm.segments.list.upload', { count: entries.length })}
        </Button>
      </div>
      {upload.error && <ErrorNotice title={problemMessage(t, asProblem(upload.error))} />}
      {upload.data && (
        <InlineNotification
          kind={Number(upload.data.invalid) > 0 || upload.data.unknownAccounts.length > 0 ? 'warning' : 'success'}
          lowContrast
          title={t('crm.segments.list.saved', { count: upload.data.saved })}
          subtitle={[
            Number(upload.data.invalid) > 0 ? t('crm.segments.list.invalid', { count: upload.data.invalid }) : '',
            upload.data.unknownAccounts.length > 0
              ? t('crm.segments.list.unknown', { numbers: upload.data.unknownAccounts.join(', ') })
              : '',
          ]
            .filter(Boolean)
            .join(' ')}
        />
      )}
    </Stack>
  );
}

/** A segment's tools: refresh now, and its people as CSV (masked; in full with the reveal permission). Every export is audited. */
export function SegmentActions({ segment }: { segment: RecordValues }) {
  const { t } = useTranslation();
  const { session } = useAdmin();
  const id = String(segment.id);
  const code = String(segment.code ?? id);
  return (
    <>
      <RefreshSegmentButton segment={segment} />
      {hasPermission(session, 'ho.crm.export') && (
        <CsvDownloadButton
          label={t('crm.segments.export.masked')}
          path="/api/v1/crm/segments/{id}/export.csv"
          params={{ id }}
          fileName={`segment-${code}.csv`}
        />
      )}
      {hasPermission(session, 'ho.crm.reveal') && (
        <CsvDownloadButton
          label={t('crm.segments.export.revealed')}
          path="/api/v1/crm/segments/{id}/export.csv"
          params={{ id }}
          query={{ reveal: true }}
          fileName={`segment-${code}-contacts.csv`}
        />
      )}
    </>
  );
}

/** "Refresh now": the segment is worked out again within minutes instead of tomorrow. */
export function RefreshSegmentButton({ segment }: { segment: RecordValues }) {
  const { t } = useTranslation();
  const { session } = useAdmin();
  const id = String(segment.id);
  const refresh = useMutation({
    mutationFn: () => hoApi.POST('/api/v1/crm/segments/{id}/refresh', { params: { path: { id } } }),
  });
  if (!hasPermission(session, 'ho.crm.manage')) return null;
  return (
    <Button
      kind="tertiary"
      renderIcon={Renew}
      disabled={refresh.isPending || refresh.isSuccess}
      onClick={() => refresh.mutate()}
    >
      {refresh.isSuccess ? t('crm.segments.refresh.asked') : t('crm.segments.refresh.button')}
    </Button>
  );
}

/** A rule segment's conditions in the rule builder, with an estimate of how many people they take in. */
export function SegmentConditionsField(props: FieldEditorProps) {
  const { t } = useTranslation();
  const format = useFormat();
  const estimate = useMutation({
    mutationFn: () =>
      unwrap(
        hoApi.POST('/api/v1/crm/segments/estimate', {
          body: { conditions: typeof props.value === 'string' ? props.value : JSON.stringify(props.value ?? {}) },
        }),
      ),
  });
  if (props.values.kind !== undefined && props.values.kind !== null && props.values.kind !== 'rules') {
    return <InlineNotification kind="info" lowContrast hideCloseButton title={t('crm.segments.conditions.notRules')} />;
  }

  return (
    <Stack gap={5}>
      <RuleBuilder {...props} kind="segment" />
      <div>
        <Button kind="tertiary" disabled={estimate.isPending} onClick={() => estimate.mutate()}>
          {t('crm.segments.estimate.button')}
        </Button>
      </div>
      {estimate.error && <ErrorNotice title={problemMessage(t, asProblem(estimate.error))} />}
      {estimate.data && (
        <InlineNotification
          kind="info"
          lowContrast
          hideCloseButton
          title={t('crm.segments.estimate.result', {
            count: format.number(estimate.data.estimate),
            people: format.number(estimate.data.people),
          })}
          subtitle={t('crm.segments.estimate.sample', { sampled: format.number(estimate.data.sampled) })}
        />
      )}
    </Stack>
  );
}
